import { create } from 'zustand';
import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc,
  serverTimestamp,
  Timestamp 
} from 'firebase/firestore';
import { 
  ref, 
  uploadBytes, 
  getDownloadURL 
} from 'firebase/storage';
import { db, storage } from '@/lib/firebase';
import type { VerificationRank } from '@kulum/shared';

// Verification badges type for profiles collection
export interface VerificationBadges {
  identity: boolean;
  education: boolean;
  income: boolean;
  spermTest: boolean;
  stdTest: boolean;
  ranks?: {
    education?: VerificationRank;
    income?: VerificationRank;
    spermTest?: VerificationRank;
    stdTest?: VerificationRank;
  };
}

// Verification status type
export type VerificationStatus = 'none' | 'pending' | 'approved' | 'rejected';

// Verification document type
export interface VerificationDocument {
  url: string;
  fileName: string;
  uploadedAt: Date;
  type?: string;
}

// Verification item type
export interface VerificationItem {
  status: VerificationStatus;
  rank?: VerificationRank;
  verifiedAt?: Date;
  expiresAt?: Date;
  documents: VerificationDocument[];
  rejectionReason?: string;
}

// Verification data type
export interface VerificationData {
  userId: string;
  identity?: VerificationItem;
  education?: VerificationItem;
  income?: VerificationItem;
  spermTest?: VerificationItem;
  stdTest?: VerificationItem;
  adminReview?: {
    status: 'pending' | 'approved' | 'rejected' | 'remanded';
    reviewedBy?: string;
    reviewedAt?: Date;
    comment?: string;
    checks?: {
      birthDateMatched?: boolean;
      incomeMatched?: boolean;
      educationMatched?: boolean;
      stdTestNegative?: boolean;
    };
    ranks?: {
      education?: VerificationRank;
      income?: VerificationRank;
      spermTest?: VerificationRank;
      stdTest?: VerificationRank;
    };
  };
  createdAt?: Date;
  updatedAt?: Date;
}

// Verification type for category
export type VerificationType = 'identity' | 'education' | 'income' | 'spermTest' | 'stdTest';

interface VerificationState {
  verification: VerificationData | null;
  isLoading: boolean;
  isUploading: boolean;
  error: string | null;

  // Actions
  fetchVerification: (userId: string) => Promise<void>;
  uploadDocument: (userId: string, type: VerificationType, file: File) => Promise<string>;
  removeDocument: (userId: string, type: VerificationType, documentUrl: string) => Promise<void>;
  submitForReview: (userId: string) => Promise<void>;
  syncVerificationBadges: (userId: string) => Promise<void>;
  clearError: () => void;
  reset: () => void;
}

// Helper to convert Firestore Timestamp to Date
const timestampToDate = (timestamp: Timestamp | undefined): Date | undefined => {
  return timestamp?.toDate();
};

// Helper to convert verification item for Firestore writes (exclude undefined fields)
const toFirestoreVerificationItem = (
  item: VerificationItem,
  options?: {
    status?: VerificationStatus;
    documents?: VerificationDocument[];
    clearReviewMeta?: boolean;
  }
): Record<string, unknown> => {
  const status = options?.status ?? item.status ?? 'none';
  const documents = options?.documents ?? item.documents ?? [];
  const clearReviewMeta = options?.clearReviewMeta === true;

  const firestoreItem: Record<string, unknown> = {
    status,
    documents: documents.map((doc) => {
      const firestoreDoc: Record<string, unknown> = {
        url: doc.url,
        fileName: doc.fileName,
        uploadedAt: Timestamp.fromDate(doc.uploadedAt),
      };

      if (typeof doc.type === 'string' && doc.type.length > 0) {
        firestoreDoc.type = doc.type;
      }

      return firestoreDoc;
    }),
  };

  if (!clearReviewMeta && item.verifiedAt instanceof Date) {
    firestoreItem.verifiedAt = Timestamp.fromDate(item.verifiedAt);
  }
  if (typeof item.rank === 'string') {
    firestoreItem.rank = item.rank;
  }
  if (!clearReviewMeta && item.expiresAt instanceof Date) {
    firestoreItem.expiresAt = Timestamp.fromDate(item.expiresAt);
  }
  if (!clearReviewMeta && typeof item.rejectionReason === 'string' && item.rejectionReason.length > 0) {
    firestoreItem.rejectionReason = item.rejectionReason;
  }

  return firestoreItem;
};

// Helper to convert verification item from Firestore
const convertVerificationItem = (item: Record<string, unknown> | undefined): VerificationItem | undefined => {
  if (!item) return undefined;
  return {
    status: (item.status as VerificationStatus) || 'none',
    rank:
      item.rank === 'S' || item.rank === 'A' || item.rank === 'B' || item.rank === 'C' || item.rank === 'D'
        ? item.rank
        : undefined,
    verifiedAt: timestampToDate(item.verifiedAt as Timestamp | undefined),
    expiresAt: timestampToDate(item.expiresAt as Timestamp | undefined),
    documents: (item.documents as Array<Record<string, unknown>> || []).map((doc) => ({
      url: doc.url as string,
      fileName: doc.fileName as string,
      uploadedAt: timestampToDate(doc.uploadedAt as Timestamp) || new Date(),
      type: doc.type as string | undefined,
    })),
    rejectionReason: item.rejectionReason as string | undefined,
  };
};

export const useVerificationStore = create<VerificationState>((set, get) => ({
  verification: null,
  isLoading: false,
  isUploading: false,
  error: null,

  fetchVerification: async (userId: string) => {
    try {
      set({ isLoading: true, error: null });
      
      const verificationRef = doc(db, 'verifications', userId);
      const verificationSnap = await getDoc(verificationRef);

      if (verificationSnap.exists()) {
        const data = verificationSnap.data();
        set({
          verification: {
            userId,
            identity: convertVerificationItem(data.identity),
            education: convertVerificationItem(data.education),
            income: convertVerificationItem(data.income),
            spermTest: convertVerificationItem(data.spermTest),
            stdTest: convertVerificationItem(data.stdTest),
            adminReview: data.adminReview ? {
              status: data.adminReview.status,
              reviewedBy: data.adminReview.reviewedBy,
              reviewedAt: timestampToDate(data.adminReview.reviewedAt),
              comment: data.adminReview.comment,
              checks: data.adminReview.checks,
              ranks: data.adminReview.ranks,
            } : undefined,
            createdAt: timestampToDate(data.createdAt),
            updatedAt: timestampToDate(data.updatedAt),
          },
        });
      } else {
        // Create empty verification document
        const emptyVerification: VerificationData = {
          userId,
          identity: { status: 'none', documents: [] },
          education: { status: 'none', documents: [] },
          income: { status: 'none', documents: [] },
          spermTest: { status: 'none', documents: [] },
          stdTest: { status: 'none', documents: [] },
        };
        
        await setDoc(verificationRef, {
          userId,
          identity: { status: 'none', documents: [] },
          education: { status: 'none', documents: [] },
          income: { status: 'none', documents: [] },
          spermTest: { status: 'none', documents: [] },
          stdTest: { status: 'none', documents: [] },
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });

        set({ verification: emptyVerification });
      }
    } catch (error) {
      console.error('Error fetching verification:', error);
      set({ error: '認証情報の取得に失敗しました' });
    } finally {
      set({ isLoading: false });
    }
  },

  uploadDocument: async (userId: string, type: VerificationType, file: File) => {
    try {
      set({ isUploading: true, error: null });

      // Create storage reference
      const ext = file.name.split('.').pop();
      const documentId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const storageRef = ref(storage, `verifications/${userId}/${type}/${documentId}.${ext}`);

      // Upload file
      await uploadBytes(storageRef, file);

      // Get download URL
      const downloadUrl = await getDownloadURL(storageRef);

      // Update verification document
      const verificationRef = doc(db, 'verifications', userId);
      const currentVerification = get().verification;
      const currentItem = currentVerification?.[type] || { status: 'none', documents: [] };

      const newDocument: VerificationDocument = {
        url: downloadUrl,
        fileName: file.name,
        uploadedAt: new Date(),
        type: file.type,
      };

      const newDocuments = [...(currentItem.documents || []), newDocument];
      const nextItem: VerificationItem = {
        ...currentItem,
        status: 'none',
        documents: newDocuments,
      };

      await updateDoc(verificationRef, {
        [type]: toFirestoreVerificationItem(nextItem, {
          status: 'none',
          documents: newDocuments,
          clearReviewMeta: true,
        }),
        updatedAt: serverTimestamp(),
      });

      // Update local state
      if (currentVerification) {
        set({
          verification: {
            ...currentVerification,
            [type]: {
              ...nextItem,
              status: 'none',
              documents: newDocuments,
              verifiedAt: undefined,
              rejectionReason: undefined,
            },
          },
        });
      }

      return downloadUrl;
    } catch (error) {
      console.error('Error uploading document:', error);
      set({ error: '書類のアップロードに失敗しました' });
      throw error;
    } finally {
      set({ isUploading: false });
    }
  },

  removeDocument: async (userId: string, type: VerificationType, documentUrl: string) => {
    try {
      set({ isUploading: true, error: null });

      const verificationRef = doc(db, 'verifications', userId);
      const currentVerification = get().verification;
      const currentItem = currentVerification?.[type] || { status: 'none', documents: [] };

      const newDocuments = currentItem.documents.filter((d) => d.url !== documentUrl);
      const newStatus = newDocuments.length === 0 ? 'none' : currentItem.status;
      const nextItem: VerificationItem = {
        ...currentItem,
        status: newStatus,
        documents: newDocuments,
      };

      await updateDoc(verificationRef, {
        [type]: toFirestoreVerificationItem(nextItem, {
          status: newStatus,
          documents: newDocuments,
        }),
        updatedAt: serverTimestamp(),
      });

      // Update local state
      if (currentVerification) {
        set({
          verification: {
            ...currentVerification,
            [type]: {
              ...nextItem,
              status: newStatus,
              documents: newDocuments,
            },
          },
        });
      }
    } catch (error) {
      console.error('Error removing document:', error);
      set({ error: '書類の削除に失敗しました' });
      throw error;
    } finally {
      set({ isUploading: false });
    }
  },

  submitForReview: async (userId: string) => {
    try {
      set({ isUploading: true, error: null });

      const verificationRef = doc(db, 'verifications', userId);
      const userRef = doc(db, 'users', userId);
      const profileRef = doc(db, 'profiles', userId);
      const currentVerification = get().verification;

      const [userSnap, profileSnap] = await Promise.all([
        getDoc(userRef),
        getDoc(profileRef),
      ]);
      if (!userSnap.exists()) {
        throw new Error('ユーザー情報が見つかりません');
      }
      if (!profileSnap.exists()) {
        throw new Error('プロフィール情報が見つかりません');
      }

      const userData = userSnap.data() as { role?: unknown; status?: unknown };
      const role = userData.role === 'donor' ? 'donor' : 'donee';
      const currentStatus = typeof userData.status === 'string' ? userData.status : 'pending';
      const requiredTypes: VerificationType[] = role === 'donor'
        ? ['identity', 'education', 'income', 'spermTest', 'stdTest']
        : ['identity'];
      const verificationLabels: Record<VerificationType, string> = {
        identity: '本人確認',
        education: '学歴証明',
        income: '年収証明',
        spermTest: '精液検査',
        stdTest: '性病検査',
      };
      const validationErrors: string[] = [];
      const missingDocumentTypes = requiredTypes.filter(
        (type) => (currentVerification?.[type]?.documents?.length || 0) === 0
      );
      if (missingDocumentTypes.length > 0) {
        validationErrors.push(
          `審査申請には${missingDocumentTypes.map((type) => verificationLabels[type]).join('・')}の書類提出が必要です`
        );
      }
      const rejectedUntil =
        typeof (userSnap.data() as { rejectedUntil?: unknown }).rejectedUntil === 'object' &&
        (userSnap.data() as { rejectedUntil?: { toDate?: () => Date } }).rejectedUntil &&
        typeof (userSnap.data() as { rejectedUntil?: { toDate?: () => Date } }).rejectedUntil?.toDate === 'function'
          ? (userSnap.data() as { rejectedUntil?: { toDate: () => Date } }).rejectedUntil?.toDate()
          : undefined;

      if (
        role === 'donor' &&
        currentStatus === 'rejected' &&
        rejectedUntil instanceof Date &&
        rejectedUntil.getTime() > Date.now()
      ) {
        validationErrors.push(`再申請は ${rejectedUntil.toLocaleDateString('ja-JP')} 以降に可能です`);
      }
      const profileData = profileSnap.data() as { basicInfo?: Record<string, unknown> };
      const basicInfo = profileData.basicInfo || {};

      if (basicInfo.birthDate === undefined || basicInfo.birthDate === null) {
        validationErrors.push('審査申請には生年月日の入力が必要です');
      }

      if (role === 'donor') {
        const annualIncome = basicInfo.annualIncome;
        const education = basicInfo.education as Record<string, unknown> | undefined;
        const educationLevel = education?.level;
        const schoolName = education?.schoolName;
        if (typeof annualIncome !== 'string' || annualIncome.length === 0) {
          validationErrors.push('審査申請には年収の入力が必要です');
        }
        if (typeof educationLevel !== 'string' || educationLevel.length === 0) {
          validationErrors.push('審査申請には学歴（最終学歴）の入力が必要です');
        }
        if (typeof schoolName !== 'string' || schoolName.length === 0) {
          validationErrors.push('審査申請には学歴（学校名）の入力が必要です');
        }
      }

      if (validationErrors.length > 0) {
        throw new Error(validationErrors.join('\n'));
      }

      const verificationUpdates: Record<string, unknown> = {};
      for (const type of requiredTypes) {
        const currentItem = currentVerification?.[type] || { status: 'none', documents: [] };
        verificationUpdates[type] = toFirestoreVerificationItem(
          {
            ...currentItem,
            status: 'pending',
            verifiedAt: undefined,
            rejectionReason: undefined,
          },
          {
            status: 'pending',
            documents: currentItem.documents || [],
            clearReviewMeta: true,
          }
        );
      }

      await updateDoc(verificationRef, {
        ...verificationUpdates,
        adminReview: {
          status: 'pending',
        },
        updatedAt: serverTimestamp(),
      });

      const userUpdates: Record<string, unknown> = {
        identityVerificationStatus: 'pending',
        updatedAt: serverTimestamp(),
      };
      if (role === 'donor') {
        userUpdates.status = 'under_review';
      } else if (currentStatus === 'pending') {
        userUpdates.status = 'email_verified';
      }
      await updateDoc(userRef, userUpdates);

      if (currentVerification) {
        const nextVerification: VerificationData = {
          ...currentVerification,
          adminReview: {
            status: 'pending',
          },
        };
        for (const type of requiredTypes) {
          const currentItem = currentVerification[type] || { status: 'none', documents: [] };
          nextVerification[type] = {
            ...currentItem,
            status: 'pending',
            verifiedAt: undefined,
            rejectionReason: undefined,
          };
        }
        set({
          verification: nextVerification,
        });
      }
    } catch (error) {
      console.error('Error submitting for review:', error);
      set({ error: error instanceof Error ? error.message : '審査申請に失敗しました' });
      throw error;
    } finally {
      set({ isUploading: false });
    }
  },

  syncVerificationBadges: async (userId: string) => {
    try {
      const currentVerification = get().verification;
      if (!currentVerification) return;

      // Build verification badges from current verification data
      const badges: VerificationBadges = {
        identity: currentVerification.identity?.status === 'approved',
        education: currentVerification.education?.status === 'approved',
        income: currentVerification.income?.status === 'approved',
        spermTest: currentVerification.spermTest?.status === 'approved',
        stdTest: currentVerification.stdTest?.status === 'approved',
        ranks: {
          education:
            currentVerification.education?.status === 'approved'
              ? currentVerification.education.rank
              : undefined,
          income:
            currentVerification.income?.status === 'approved'
              ? currentVerification.income.rank
              : undefined,
          spermTest:
            currentVerification.spermTest?.status === 'approved'
              ? currentVerification.spermTest.rank
              : undefined,
          stdTest:
            currentVerification.stdTest?.status === 'approved'
              ? currentVerification.stdTest.rank
              : undefined,
        },
      };

      // Update profiles collection
      const profileRef = doc(db, 'profiles', userId);
      await updateDoc(profileRef, {
        verificationBadges: badges,
        updatedAt: serverTimestamp(),
      });

      console.log('Verification badges synced to profiles:', badges);
    } catch (error) {
      console.error('Error syncing verification badges:', error);
      // Don't set error state, this is a background sync operation
    }
  },

  clearError: () => set({ error: null }),
  
  reset: () => set({
    verification: null,
    isLoading: false,
    isUploading: false,
    error: null,
  }),
}));
